import { QueryObserverResult, RefetchOptions } from '@tanstack/react-query';
import { QuestionnaireItemAnswerOption, QuestionnaireResponse, QuestionnaireResponseItem } from 'fhir/r4b';
import { createContext, useContext } from 'react';
import { useOutletContext } from 'react-router-dom';
import { HandleAnswerInput, StartInterviewInput } from 'utils/lib/types/api/ai-interview.types';
import { GetInsuranceCardSuggestionsResponse } from 'utils/lib/types/api/get-insurance-card-suggestions.types';
import { GetPhotoIdSuggestionsResponse } from 'utils/lib/types/api/get-photo-id-suggestions.types';
import {
  AppointmentSummary,
  IntakeQuestionnaireItem,
  PaperworkPatient,
  QuestionnaireFormFields,
  UCGetPaperworkResponse,
} from 'utils/lib/types/data/paperwork/paperwork.types';
import {
  CreditCardInfo,
  PaymentMethodSetDefaultParameters,
  PaymentMethodSetupZambdaOutput,
} from 'utils/lib/types/data/payment/payment-method-types';
import { SearchPlacesInput, SearchPlacesOutput } from 'utils/lib/types/data/search-places';
import { GetAnswerOptionsRequest } from 'utils/lib/types/data/telemed/appointments/appointments.types';

/** Identifies which just-uploaded file a card-suggestions call is for, so the caller can cache the result keyed appropriately (e.g. primary vs secondary insurance). */
export interface CardSuggestionsInput {
  appointmentID: string;
  fileURL: string;
  fileContentType?: string;
  cardSlot: string;
}

export interface PaperworkComponentHelpers {
  /** PharmacyCollection */
  handleSearchPlaces: ((input: SearchPlacesInput) => Promise<SearchPlacesOutput>) | undefined;
  /** FileInput */
  createZ3Object:
    | ((input: { appointmentID: string; fileType: string; fileFormat: string; file: File }) => Promise<any>)
    | undefined;
  /** FileInput (insurance card front) */
  getInsuranceCardSuggestions:
    | ((input: CardSuggestionsInput) => Promise<GetInsuranceCardSuggestionsResponse>)
    | undefined;
  /** FileInput (photo ID front) */
  getPhotoIdSuggestions: ((input: CardSuggestionsInput) => Promise<GetPhotoIdSuggestionsResponse>) | undefined;
  /** AiInterview */
  aIInterviewStart: ((input: StartInterviewInput) => Promise<QuestionnaireResponse>) | undefined;
  /** AiInterview */
  aIInterviewHandleAnswer: ((input: HandleAnswerInput) => Promise<QuestionnaireResponse>) | undefined;
  /** CreditCardVerification */
  setDefaultPaymentMethod: ((input: PaymentMethodSetDefaultParameters) => Promise<unknown>) | undefined;
  /** FreeMultiSelectInput */
  getAnswerOptions: ((input: GetAnswerOptionsRequest) => Promise<QuestionnaireItemAnswerOption[]>) | undefined;
}

export interface PaperworkContext
  extends Omit<UCGetPaperworkResponse, 'patient' | 'appointment' | 'questionnaireResponse'> {
  paperwork: QuestionnaireResponseItem[];
  paperworkInProgress: { [pageId: string]: QuestionnaireFormFields };
  pageItems: IntakeQuestionnaireItem[];
  pages: IntakeQuestionnaireItem[];
  appointment: AppointmentSummary | undefined;
  patient: PaperworkPatient | undefined;
  questionnaireResponse: QuestionnaireResponse | undefined;
  cardsAreLoading: boolean;
  paymentMethodStateInitializing: boolean;
  paymentMethods: CreditCardInfo[];
  stripeSetupData: PaymentMethodSetupZambdaOutput | undefined;
  setContinueLabel?: (label: string | undefined) => void;
  continueLabel?: string;
  saveButtonDisabled?: boolean;
  refetchPaymentMethods: (options?: RefetchOptions | undefined) => Promise<
    QueryObserverResult<
      {
        cards: CreditCardInfo[];
      },
      Error
    >
  >;
  refetchSetupData: (
    options?: RefetchOptions | undefined
  ) => Promise<QueryObserverResult<PaymentMethodSetupZambdaOutput, Error>>;
  setSaveButtonDisabled: (newVal: boolean) => void;
  findAnswerWithLinkId: (linkId: string) => QuestionnaireResponseItem | undefined;
  paperworkComponentHelpers: PaperworkComponentHelpers;
}

// The main intake paperwork flow supplies this context through the router `<Outlet context={...}>`.
// Standalone consumers that live outside that outlet (e.g. the managed standalone form) can instead
// wrap their tree in `<PaperworkProvider value={...}>`. `usePaperworkContext` prefers an explicit
// provider and falls back to the outlet, so the existing flow is untouched.
const PaperworkReactContext = createContext<PaperworkContext | null>(null);
export const PaperworkProvider = PaperworkReactContext.Provider;

export const usePaperworkContext = (): PaperworkContext => {
  // Both hooks are called unconditionally (rules-of-hooks); we just pick which value to return.
  // `useOutletContext` returns null rather than throwing when there is no enclosing outlet.
  const fromProvider = useContext(PaperworkReactContext);
  const fromOutlet = useOutletContext<PaperworkContext>();
  return fromProvider ?? fromOutlet;
};
